import { Control, Controller, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Autocomplete, Box, MenuItem, Typography } from '@mui/material'
import ReactQuill from 'react-quill'
//@ts-ignore
import 'react-quill/dist/quill.snow.css'
import { z } from 'zod'
import { NewsType } from 'src/enums'
import { IFile } from 'src/generated-types'
import { INewsArticle } from 'src/generated-types/news'
import BasicDatePicker from 'src/components/DateTimePicker'
import {
  AppForm,
  AppFormProps,
  FormCheckbox,
  FormFileCoreUpload,
  FormSelectField,
  FormSubmitButton,
  FormTextField,
} from 'src/components/FormCore'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { useZodForm } from 'src/hooks/zod-form'
import { EnumToSelectTypes } from 'src/generated-types/select-type'
import { AppTextField } from 'src/components/AppTextField'
import { sortByName } from 'src/utils'
import { getUTCDate } from 'src/utils/function'

type TargetMode = 'all' | 'localities' | 'localityGroups' | 'serviceStations' | 'storages'

const isQuillEmpty = (value: string) => !value || value === '<p><br></p>' || value.replace(/<[^>]*>/g, '').trim() === ''

export const newsArticleSchema = z
  .object({
    title: z.string().min(1),
    content: z
      .string()
      .min(1)
      .refine(v => !isQuillEmpty(v), { message: 'required' }),
    newsType: z.nativeEnum(NewsType),
    targetMode: z.enum(['all', 'localities', 'localityGroups', 'serviceStations', 'storages']),
    localityGroupIds: z.array(z.number()).optional(),
    localityIds: z.array(z.number()).optional(),
    serviceStationIds: z.array(z.number()).optional(),
    storageIds: z.array(z.number()).optional(),
    fileIds: z.array(z.union([z.string(), z.number()])).optional(),
    expiresAt: z
      .date()
      .transform(val => getUTCDate(new Date(val)))
      .nullable()
      .optional(),
    isImportant: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.targetMode === 'localities' && (!data.localityIds || data.localityIds.length === 0)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'required', path: ['localityIds'] })
    }
    if (data.targetMode === 'localityGroups' && (!data.localityGroupIds || data.localityGroupIds.length === 0)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'required', path: ['localityGroupIds'] })
    }
    if (data.targetMode === 'serviceStations' && (!data.serviceStationIds || data.serviceStationIds.length === 0)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'required', path: ['serviceStationIds'] })
    }
    if (data.targetMode === 'storages' && (!data.storageIds || data.storageIds.length === 0)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'required', path: ['storageIds'] })
    }
  })

export type NewsArticleFormSchema = z.infer<typeof newsArticleSchema>

export interface NewsArticleFormValues {
  title: string
  content: string
  newsType: NewsType
  isGlobal: boolean
  localityGroupIds: number[]
  localityIds: number[]
  serviceStationIds: number[]
  storageIds: number[]
  fileIds: number[]
  expiresAt?: Date | null
  isImportant: boolean
}

export interface NewsArticleFormProps extends AppFormProps {
  onSubmit: (values: NewsArticleFormValues) => void
  onCancel: () => void
  title: string
  initialValues?: INewsArticle
  localityGroups?: Array<{ id: number; name: string }>
  localities?: Array<{ id: number; name: string }>
  serviceStations?: Array<{ id: number; name: string }>
  storages?: Array<{ id: number; name: string }>
  isLoading?: boolean
  topActions?: React.ReactNode[]
  hideTargetMode?: boolean
  disableGlobalTarget?: boolean
  hasLocalities?: boolean
  hasServiceStations?: boolean
  hasStorages?: boolean
}

const getFirstAvailableTarget = (
  disableGlobalTarget: boolean,
  hasLocalities?: boolean,
  hasServiceStations?: boolean,
  hasStorages?: boolean,
): TargetMode => {
  if (!disableGlobalTarget) return 'all'
  if (hasLocalities !== false) return 'localities'
  if (hasServiceStations !== false) return 'serviceStations'
  if (hasStorages !== false) return 'storages'
  return 'localities'
}

const getInitialTargetMode = (
  initialValues?: INewsArticle,
  disableGlobalTarget = false,
  hasLocalities?: boolean,
  hasServiceStations?: boolean,
  hasStorages?: boolean,
): TargetMode => {
  if (!initialValues)
    return getFirstAvailableTarget(disableGlobalTarget, hasLocalities, hasServiceStations, hasStorages)
  if (initialValues.isGlobal && !disableGlobalTarget) return 'all'
  if ((initialValues.newsArticleLocalities?.length ?? 0) > 0) return 'localities'
  if ((initialValues.newsArticleLocalityGroups?.length ?? 0) > 0) return 'localityGroups'
  if ((initialValues.newsArticleServiceStations?.length ?? 0) > 0) return 'serviceStations'
  if ((initialValues.newsArticleStorages?.length ?? 0) > 0) return 'storages'
  return getFirstAvailableTarget(disableGlobalTarget, hasLocalities, hasServiceStations, hasStorages)
}

type MultiSelectItem = { id: number; name: string }

const TargetMultiSelect = ({
  control,
  name,
  options,
  label,
  helperText,
  withSelectAll = false,
  translate,
}: {
  control: Control<NewsArticleFormSchema>
  name: 'localityGroupIds' | 'localityIds' | 'serviceStationIds' | 'storageIds'
  options: MultiSelectItem[]
  label: string
  helperText: string
  withSelectAll?: boolean
  translate: (key: string, opts?: any) => string
}) => {
  const SELECT_ALL_ID = -1
  const sortedOptions = [...options].sort(sortByName)
  const allIds = sortedOptions.map(o => o.id)
  const autocompleteOptions: MultiSelectItem[] = withSelectAll
    ? [{ id: SELECT_ALL_ID, name: '' }, ...sortedOptions]
    : sortedOptions

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const allSelected = withSelectAll && allIds.length > 0 && allIds.every(id => (field.value ?? []).includes(id))

        return (
          <Autocomplete
            multiple
            value={sortedOptions.filter(o => (field.value ?? []).includes(o.id))}
            onChange={(_, selected) => {
              if (withSelectAll && (selected as MultiSelectItem[]).some(s => s.id === SELECT_ALL_ID)) {
                field.onChange(allSelected ? [] : allIds)
              } else {
                field.onChange((selected as MultiSelectItem[]).map(o => o.id))
              }
            }}
            options={autocompleteOptions}
            autoHighlight
            getOptionLabel={o =>
              withSelectAll && o.id === SELECT_ALL_ID ? translate('select-all', { ns: 'common' }) : o.name
            }
            renderOption={(props, o) => (
              <MenuItem
                {...props}
                key={o.id}
                value={o.id}
                sx={withSelectAll && o.id === SELECT_ALL_ID ? { fontWeight: 600 } : undefined}
              >
                {withSelectAll && o.id === SELECT_ALL_ID
                  ? allSelected
                    ? translate('deselect-all', { ns: 'common' })
                    : translate('select-all', { ns: 'common' })
                  : o.name}
              </MenuItem>
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={params => (
              <AppTextField
                {...params}
                label={label}
                error={!!fieldState.error}
                helperText={fieldState.error ? translate('required', { ns: 'common' }) : helperText}
              />
            )}
          />
        )
      }}
    />
  )
}

export const NewsArticleForm = ({
  onSubmit,
  onCancel,
  title,
  initialValues,
  localityGroups = [],
  localities = [],
  serviceStations = [],
  storages = [],
  isLoading = false,
  topActions,
  hideTargetMode = false,
  disableGlobalTarget = false,
  hasLocalities,
  hasServiceStations,
  hasStorages,
}: NewsArticleFormProps) => {
  const { t: translate } = useTranslation(['common', 'news'])
  const form = useZodForm(newsArticleSchema, {
    defaultValues: {
      title: initialValues?.title ?? '',
      content: initialValues?.content ?? '',
      newsType: initialValues?.newsType ?? NewsType.General,
      targetMode: getInitialTargetMode(
        initialValues,
        disableGlobalTarget,
        hasLocalities,
        hasServiceStations,
        hasStorages,
      ),
      localityGroupIds: initialValues?.newsArticleLocalityGroups?.map(g => g.localityGroupId) ?? [],
      localityIds: initialValues?.newsArticleLocalities?.map(l => l.localityId) ?? [],
      serviceStationIds: initialValues?.newsArticleServiceStations?.map(s => s.serviceStationId) ?? [],
      storageIds: initialValues?.newsArticleStorages?.map(s => s.storageId) ?? [],
      fileIds: [],
      expiresAt: initialValues?.expiresAt ? new Date(initialValues.expiresAt) : null,
      isImportant: initialValues?.isImportant ?? false,
    },
  })

  const targetMode = useWatch({ control: form.control, name: 'targetMode' })

  const handleSubmit = (data: NewsArticleFormSchema) => {
    onSubmit({
      title: data.title,
      content: data.content,
      newsType: data.newsType,
      isGlobal: data.targetMode === 'all',
      localityGroupIds: data.targetMode === 'localityGroups' ? data.localityGroupIds ?? [] : [],
      localityIds: data.targetMode === 'localities' ? data.localityIds ?? [] : [],
      serviceStationIds: data.targetMode === 'serviceStations' ? data.serviceStationIds ?? [] : [],
      storageIds: data.targetMode === 'storages' ? data.storageIds ?? [] : [],
      fileIds: (data.fileIds ?? []).map(Number),
      expiresAt: data.expiresAt ?? null,
      isImportant: data.isImportant ?? false,
    })
  }

  const newsTypes = EnumToSelectTypes(NewsType, 'news')

  const targetModes = [
    ...(!disableGlobalTarget ? [{ id: 'all' as TargetMode, value: translate('global-news', { ns: 'common' }) }] : []),
    ...(hasLocalities !== false
      ? [{ id: 'localities' as TargetMode, value: translate('localities', { ns: 'common' }) }]
      : []),
    ...(hasLocalities !== false && localityGroups.length > 0
      ? [{ id: 'localityGroups' as TargetMode, value: translate('locality-groups', { ns: 'locality-group' }) }]
      : []),
    ...(hasServiceStations !== false
      ? [{ id: 'serviceStations' as TargetMode, value: translate('service-stations', { ns: 'news' }) }]
      : []),
    ...(hasStorages !== false ? [{ id: 'storages' as TargetMode, value: translate('storages', { ns: 'news' }) }] : []),
  ]

  return (
    <AppForm form={form} onSubmit={handleSubmit} hasInitialValues={!!initialValues}>
      <GenericDialogLayout
        title={title}
        pop={onCancel}
        removedCloseButton={false}
        topActions={topActions}
        actions={[<FormSubmitButton key="submit" isLoading={isLoading} />]}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <FormTextField fullWidth name="title" label={translate('title', { ns: 'common' })} />

          <Box>
            <Controller
              control={form.control}
              name="content"
              render={({ field, fieldState }) => (
                <>
                  <Box
                    sx={{
                      '& .ql-container': { minHeight: '150px', fontSize: '14px' },
                      '& .ql-editor': { minHeight: '150px', fontWeight: 400 },
                      padding: '0 8px 12px',
                      '& .ql-editor-strong': { fontWeight: 700 },
                    }}
                  >
                    <ReactQuill
                      value={field.value}
                      onChange={field.onChange}
                      modules={{
                        clipboard: {
                          matchVisual: true,
                        },
                      }}
                      theme="snow"
                      placeholder={translate('content', { ns: 'common' }) as string}
                    />
                  </Box>
                  {fieldState.error && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', ml: 1.75 }}>
                      {translate('required', { ns: 'common' })}
                    </Typography>
                  )}
                </>
              )}
            />
          </Box>

          <Controller
            control={form.control}
            name="newsType"
            render={({ field, fieldState }) => (
              <Autocomplete
                options={newsTypes}
                getOptionLabel={o => o.value}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                value={newsTypes.find(t => Number(t.id) === field.value) ?? null}
                onChange={(_, val) => field.onChange(val != null ? Number((val as any).id) : NewsType.General)}
                renderOption={(props, o) => (
                  <MenuItem {...props} key={o.value} value={o.id}>
                    {o.value}
                  </MenuItem>
                )}
                renderInput={params => (
                  <AppTextField
                    {...params}
                    label={translate('type', { ns: 'common' })}
                    error={!!fieldState.error}
                    helperText={fieldState.error ? translate('required', { ns: 'common' }) : undefined}
                  />
                )}
              />
            )}
          />

          {!hideTargetMode && targetModes.length > 1 && (
            <FormSelectField
              name="targetMode"
              data={targetModes}
              label={translate('target', { ns: 'common' })}
              getOptionLabel={option => option.value}
              renderOption={(props: any, option) => (
                <Box {...props} key={option.id}>
                  {option.value}
                </Box>
              )}
              isOptionEqualToValue={(o, v) => o.id === v?.id}
              value={targetModes.find(m => m.id === targetMode) ?? null}
              onChange={(_, val) => form.setValue('targetMode', (val as { id: TargetMode })?.id ?? targetModes[0].id)}
            />
          )}

          {!hideTargetMode && targetMode === 'localityGroups' && (
            <TargetMultiSelect
              control={form.control}
              name="localityGroupIds"
              options={localityGroups}
              label={translate('locality-groups', { ns: 'locality-group' })}
              helperText={translate('select-target-groups', { ns: 'common' })}
              translate={translate}
              withSelectAll
            />
          )}

          {!hideTargetMode && targetMode === 'localities' && (
            <TargetMultiSelect
              control={form.control}
              name="localityIds"
              options={localities}
              label={translate('localities', { ns: 'common' })}
              helperText={translate('select-target-localities', { ns: 'common' })}
              withSelectAll
              translate={translate}
            />
          )}

          {!hideTargetMode && targetMode === 'serviceStations' && (
            <TargetMultiSelect
              control={form.control}
              name="serviceStationIds"
              options={serviceStations}
              label={translate('service-stations', { ns: 'news' })}
              helperText={translate('select-target-service-stations', { ns: 'common' })}
              withSelectAll
              translate={translate}
            />
          )}

          {!hideTargetMode && targetMode === 'storages' && (
            <TargetMultiSelect
              control={form.control}
              name="storageIds"
              options={storages}
              label={translate('storages', { ns: 'news' })}
              helperText={translate('select-target-storages', { ns: 'common' })}
              translate={translate}
              withSelectAll
            />
          )}

          <BasicDatePicker name="expiresAt" label={translate('expires-at', { ns: 'common' })} clearable allowNullable />

          <FormCheckbox name="isImportant" label={translate('is-important', { ns: 'news' })} />

          <Box>
            <FormFileCoreUpload
              name="fileIds"
              multiple
              isDeletable
              mapFileKey="id"
              initialFiles={(initialValues?.files as unknown as IFile[]) ?? []}
              height="120px"
              iconHeight="30px"
              iconWidth="30px"
              titleFontSize="14px"
              subtitleFontSize="14px"
              acceptedFileTypes={['image/jpeg', 'image/png', 'image/gif', 'image/webp']}
              userMessage={translate('image-upload-user-message', { ns: 'common' })}
            />
          </Box>
        </Box>
      </GenericDialogLayout>
    </AppForm>
  )
}
